import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { collection, query, getDocs, doc, setDoc, deleteDoc, onSnapshot, addDoc, orderBy } from 'firebase/firestore';
import { Users, UserPlus, Heart, ArrowLeft, Search, UserMinus, Ticket, MessageCircle, Gift, X, Check } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { cn } from '../lib/utils';
import { UserData } from '../hooks/useUser';
import { useTranslation } from 'react-i18next';

interface GiftData {
  id: string;
  fromUid: string;
  fromName: string;
  message: string;
  timestamp: string;
  claimed: boolean;
}

interface FriendsProps {
  user: UserData;
  onBack: () => void;
  onSendHeart: (uid: string, message: string) => Promise<boolean>;
  onClaimGift: (giftId: string) => Promise<boolean>;
}

export default function Friends({ user, onBack, onSendHeart, onClaimGift }: FriendsProps) {
  const { t } = useTranslation();
  const [friends, setFriends] = useState<UserData[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<UserData[]>([]);
  const [loading, setLoading] = useState(true);
  const [isSearching, setIsSearching] = useState(false);
  const [receivedGifts, setReceivedGifts] = useState<GiftData[]>([]);
  const [activeTab, setActiveTab] = useState<'friends' | 'gifts'>('friends');
  const [sendingTo, setSendingTo] = useState<UserData | null>(null);
  const [messageInput, setMessageInput] = useState('');
  const [isSending, setIsSending] = useState(false);

  useEffect(() => {
    const unsubscribeFriends = onSnapshot(collection(db, 'users', user.uid, 'friends'), async (snap) => {
      const friendIds = snap.docs.map(doc => doc.id);
      if (friendIds.length === 0) {
        setFriends([]);
        setLoading(false);
        return;
      }

      // Fetch friend details
      const friendData: UserData[] = [];
      const q = query(collection(db, 'users'));
      const d = await getDocs(q);
      
      for (const id of friendIds) {
        const found = d.docs.find(doc => doc.id === id);
        if (found) friendData.push(found.data() as UserData);
      }
      setFriends(friendData);
      setLoading(false);
    });

    const unsubscribeGifts = onSnapshot(
      query(collection(db, 'users', user.uid, 'gifts'), orderBy('timestamp', 'desc')),
      (snap) => {
        const gifts = snap.docs.map(doc => ({ id: doc.id, ...doc.data() } as GiftData));
        setReceivedGifts(gifts);
      }
    );

    return () => {
      unsubscribeFriends();
      unsubscribeGifts();
    };
  }, [user.uid]);

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    setIsSearching(true);
    try {
      const q = query(collection(db, 'users'));
      const snap = await getDocs(q);
      const results = snap.docs
        .map(doc => doc.data() as UserData)
        .filter(u => u.uid !== user.uid && (
          u.displayName?.toLowerCase().includes(searchQuery.toLowerCase()) || 
          u.nickname?.toLowerCase().includes(searchQuery.toLowerCase())
        ));
      setSearchResults(results);
    } catch (err) {
      console.error(err);
    } finally {
      setIsSearching(false);
    }
  };

  const addFriend = async (friendUid: string) => {
    await setDoc(doc(db, 'users', user.uid, 'friends', friendUid), { friendUid });
    await setDoc(doc(db, 'users', friendUid, 'friends', user.uid), { friendUid: user.uid });
  };

  const removeFriend = async (friendUid: string) => {
    await deleteDoc(doc(db, 'users', user.uid, 'friends', friendUid));
  };

  const handleSendGift = async () => {
    if (!sendingTo) return;
    setIsSending(true);
    const success = await onSendHeart(sendingTo.uid, messageInput);
    if (success) {
      setSendingTo(null);
      setMessageInput('');
    }
    setIsSending(false);
  };

  return (
    <div className="flex flex-col h-full bg-[#030712] text-white p-6 relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-[0.05] pointer-events-none" style={{ backgroundImage: 'radial-gradient(#22d3ee 2px, transparent 2px)', backgroundSize: '32px 32px' }} />

      <div className="flex items-center justify-between mb-8 relative z-10">
        <div className="flex items-center gap-4">
          <button onClick={onBack} className="p-3 bg-slate-800 rounded-2xl cartoon-border cartoon-shadow-sm active:cartoon-shadow-active-sm transition-all border-slate-700">
            <ArrowLeft className="w-6 h-6 text-white" />
          </button>
          <h1 className="text-3xl font-num font-black tracking-tight text-white">{t('friends.title')}</h1>
        </div>
        <div className="flex gap-2 bg-slate-900 p-1 rounded-2xl cartoon-border border-slate-800">
          <button
            onClick={() => setActiveTab('friends')}
            className={cn(
              "px-4 py-2 rounded-xl text-xs font-black uppercase tracking-widest transition-all",
              activeTab === 'friends' ? "bg-primary text-white" : "text-slate-600"
            )}
          >
            {t('friends.tab_friends')}
          </button>
          <button
            onClick={() => setActiveTab('gifts')}
            className={cn(
              "px-4 py-2 rounded-xl text-xs font-black uppercase tracking-widest transition-all flex items-center gap-2",
              activeTab === 'gifts' ? "bg-secondary text-white" : "text-slate-600"
            )}
          >
            {t('friends.tab_gifts')}
            {receivedGifts.filter(g => !g.claimed).length > 0 && (
              <span className="w-2 h-2 bg-primary rounded-full animate-pulse" />
            )}
          </button>
        </div>
      </div>

      {activeTab === 'friends' ? (
        <>
          {/* Search Bar */}
          <div className="flex gap-2 mb-8 relative z-10">
            <div className="flex-1 relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={t('friends.search_placeholder')}
                className="w-full bg-slate-900 border-2 border-slate-800 rounded-2xl py-4 pl-12 pr-4 text-sm font-black focus:outline-none focus:border-primary transition-all placeholder:text-slate-600 text-white cartoon-border"
              />
            </div>
            <button
              onClick={handleSearch}
              disabled={isSearching}
              className="bg-accent px-6 rounded-2xl font-black text-xs uppercase tracking-widest text-dark transition-all active:cartoon-shadow-active-sm cartoon-border cartoon-shadow-sm disabled:opacity-50"
            >
              {isSearching ? '...' : t('friends.search_button')}
            </button>
          </div>

          {/* Search Results */}
          {searchResults.length > 0 && (
            <div className="mb-8 relative z-10">
              <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-4 px-2">{t('friends.search_results')}</h3>
              <div className="space-y-3">
                {searchResults.map((u) => (
                  <div key={u.uid} className="flex items-center gap-4 p-4 bg-slate-900 rounded-2xl cartoon-border cartoon-shadow-sm border-slate-800">
                    <img src={u.photoURL || `https://api.dicebear.com/7.x/avataaars/svg?seed=${u.uid}`} className="w-12 h-12 rounded-xl cartoon-border border-2 border-slate-800" alt="Avatar" />
                    <div className="flex-1 font-black text-sm text-white uppercase tracking-tight">{u.nickname || u.displayName}</div>
                    <button
                      onClick={() => addFriend(u.uid)}
                      className="p-3 bg-secondary text-white rounded-xl transition-all active:cartoon-shadow-active-sm cartoon-border cartoon-shadow-sm"
                    >
                      <UserPlus className="w-5 h-5" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Friends List */}
          <div className="flex-1 overflow-y-auto pr-2 scrollbar-none relative z-10">
            <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-4 px-2">{t('friends.your_friends', { count: friends.length })}</h3>
            {loading ? (
              <div className="text-center text-slate-400 font-black uppercase tracking-widest py-8">{t('friends.loading')}</div>
            ) : friends.length === 0 ? (
              <div className="text-center text-slate-300 py-12">
                <Users className="w-16 h-16 mx-auto mb-4 opacity-20" />
                <p className="font-black uppercase tracking-widest">{t('friends.no_friends_title')}</p>
                <p className="text-[10px] mt-1 font-bold uppercase tracking-widest">{t('friends.no_friends_sub')}</p>
              </div>
            ) : (
              <div className="space-y-3">
                {friends.map((f) => (
                  <div key={f.uid} className="flex items-center gap-4 p-4 bg-slate-900 rounded-2xl cartoon-border cartoon-shadow-sm group border-slate-800">
                    <img src={f.photoURL || `https://api.dicebear.com/7.x/avataaars/svg?seed=${f.uid}`} className="w-12 h-12 rounded-xl cartoon-border border-2 border-slate-800" alt="Avatar" />
                    <div className="flex-1">
                      <div className="font-black text-sm text-white uppercase tracking-tight">{f.nickname || f.displayName}</div>
                      <div className="text-[10px] text-slate-500 font-black uppercase tracking-widest">LV.{f.level}</div>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => setSendingTo(f)}
                        className="p-3 bg-slate-800 text-primary rounded-xl transition-all active:cartoon-shadow-active-sm cartoon-border cartoon-shadow-sm border-slate-700"
                        title="Send Heart Ticket"
                      >
                        <Ticket className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => removeFriend(f.uid)}
                        className="p-3 text-slate-300 hover:text-primary transition-colors"
                        title="Remove Friend"
                      >
                        <UserMinus className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      ) : (
        <div className="flex-1 overflow-y-auto pr-2 scrollbar-none relative z-10">
          <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-4 px-2">{t('friends.received_gifts')}</h3>
          {receivedGifts.length === 0 ? (
            <div className="text-center text-slate-300 py-12">
              <Gift className="w-16 h-16 mx-auto mb-4 opacity-20" />
              <p className="font-black uppercase tracking-widest">{t('friends.no_gifts_title')}</p>
            </div>
          ) : (
            <div className="space-y-3">
              {receivedGifts.map((gift) => (
                <div key={gift.id} className={cn(
                  "p-4 rounded-2xl cartoon-border cartoon-shadow-sm transition-all",
                  gift.claimed ? "bg-slate-950 opacity-60 border-slate-900" : "bg-slate-900 border-slate-800"
                )}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <div className="p-2 bg-primary/10 rounded-lg">
                        <Heart className="w-4 h-4 text-primary fill-current" />
                      </div>
                      <span className="font-black text-sm uppercase tracking-tight">{gift.fromName}</span>
                    </div>
                    <span className="text-[8px] font-black text-slate-400 uppercase">{new Date(gift.timestamp).toLocaleDateString()}</span>
                  </div>
                  {gift.message && (
                    <div className="bg-slate-950 p-3 rounded-xl mb-4 text-xs font-bold text-slate-400 italic border border-slate-800">
                      "{gift.message}"
                    </div>
                  )}
                  {!gift.claimed && (
                    <button
                      onClick={() => onClaimGift(gift.id)}
                      className="w-full py-3 bg-secondary text-white rounded-xl font-black text-xs uppercase tracking-widest cartoon-border cartoon-shadow-sm active:cartoon-shadow-active-sm transition-all flex items-center justify-center gap-2"
                    >
                      <Check className="w-4 h-4" />
                      {t('friends.claim_heart')}
                    </button>
                  )}
                  {gift.claimed && (
                    <div className="text-center text-[10px] font-black text-slate-400 uppercase tracking-widest">
                      {t('friends.claimed')}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Send Gift Modal */}
      <AnimatePresence>
        {sendingTo && (
          <div className="absolute inset-0 z-[100] flex items-center justify-center p-6">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setSendingTo(null)}
              className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
            />
            <motion.div
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              className="bg-slate-900 w-full max-w-sm rounded-[40px] p-8 cartoon-border cartoon-shadow relative z-10 border-slate-800"
            >
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-2xl font-num tracking-tight text-white">{t('friends.send_gift.title')}</h3>
                <button onClick={() => setSendingTo(null)} className="p-2 hover:bg-slate-800 rounded-full transition-colors">
                  <X className="w-6 h-6 text-slate-500" />
                </button>
              </div>

              <div className="flex items-center gap-4 mb-6 p-4 bg-slate-950 rounded-2xl border border-slate-800">
                <img src={sendingTo.photoURL || `https://api.dicebear.com/7.x/avataaars/svg?seed=${sendingTo.uid}`} className="w-12 h-12 rounded-xl border-2 border-slate-800 shadow-sm" alt="Avatar" />
                <div>
                  <div className="font-black text-sm text-white uppercase tracking-tight">{sendingTo.nickname || sendingTo.displayName}</div>
                  <div className="text-[10px] text-slate-500 font-black uppercase tracking-widest">LV.{sendingTo.level}</div>
                </div>
              </div>

              <div className="mb-6">
                <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 px-1">{t('friends.send_gift.message_label')}</label>
                <textarea
                  value={messageInput}
                  onChange={(e) => setMessageInput(e.target.value)}
                  placeholder={t('friends.send_gift.message_placeholder')}
                  maxLength={100}
                  className="w-full bg-slate-950 border-2 border-slate-800 rounded-2xl p-4 text-sm font-bold focus:outline-none focus:border-primary transition-all placeholder:text-slate-600 text-white h-32 resize-none"
                />
                <div className="text-right text-[8px] font-black text-slate-400 mt-1 uppercase tracking-widest">
                  {messageInput.length}/100
                </div>
              </div>

              <button
                onClick={handleSendGift}
                disabled={isSending}
                className="w-full bg-primary text-white font-num text-2xl py-5 rounded-3xl cartoon-border cartoon-shadow active:cartoon-shadow-active transition-all flex items-center justify-center gap-3 disabled:opacity-50"
              >
                {isSending ? t('friends.send_gift.sending') : (
                  <>
                    <Ticket className="w-6 h-6" />
                    {t('friends.send_gift.send')}
                  </>
                )}
              </button>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}
